import { Component, type ErrorInfo, type ReactNode } from 'react';
import { Alert, Box, Button, Paper, Stack, Typography } from '@mui/material';

type AppErrorBoundaryProps = {
  children: ReactNode;
};

type AppErrorBoundaryState = {
  error: Error | null;
  errorInfo: ErrorInfo | null;
};

export class AppErrorBoundary extends Component<AppErrorBoundaryProps, AppErrorBoundaryState> {
  state: AppErrorBoundaryState = { error: null, errorInfo: null };

  static getDerivedStateFromError(error: Error): Partial<AppErrorBoundaryState> {
    return { error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    this.setState({ error, errorInfo });
    console.error('CourtCaptain render error', error, errorInfo);
  }

  render() {
    const { error, errorInfo } = this.state;
    if (!error) return this.props.children;
    return (
      <Box sx={{ minHeight: '100vh', display: 'grid', placeItems: 'center', bgcolor: 'background.default', p: 2 }}>
        <Paper variant="outlined" sx={{ width: '100%', maxWidth: 720, p: 3 }}>
          <Stack spacing={2}>
            <Typography variant="h5" sx={{ fontWeight: 800 }}>CourtCaptain could not finish loading</Typography>
            <Alert severity="error">
              {error.message || 'A browser-side startup error occurred.'}
            </Alert>
            {errorInfo?.componentStack && (
              <Box component="pre" sx={{ whiteSpace: 'pre-wrap', overflow: 'auto', maxHeight: 260, bgcolor: 'grey.100', p: 2, borderRadius: 1, fontSize: '0.8rem' }}>
                {errorInfo.componentStack}
              </Box>
            )}
            <Stack direction="row" spacing={1}>
              <Button variant="contained" onClick={() => window.location.reload()}>
                Reload
              </Button>
              <Button variant="outlined" onClick={() => {
                try {
                  Object.keys(window.localStorage)
                    .filter((key) => /^sb-.+-auth-token$/.test(key))
                    .forEach((key) => window.localStorage.removeItem(key));
                  Object.keys(window.sessionStorage)
                    .filter((key) => /^sb-.+-auth-token$/.test(key))
                    .forEach((key) => window.sessionStorage.removeItem(key));
                } catch {
                  // Ignore storage errors; reload still gives Supabase a clean startup pass.
                }
                window.location.assign('/');
              }}>
                Reset Login Session
              </Button>
            </Stack>
          </Stack>
        </Paper>
      </Box>
    );
  }
}
